import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Refine
import { useTranslation } from '@refinedev/core';
// Mantine
import { Button, Flex, NativeSelect, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
// API
import { searchTariffs } from 'api/resource';
// Icons
import { IconPlus } from '@tabler/icons-react';
// Components
import { ConfirmDialog } from 'components/common';
import { ModalTariffSelectTable } from './ModalTariffSelectTable';
import { Claim, Debit, Payor, Provider, Tariff, isTariff } from 'interfaces';
// Hooks
import { ClaimBulkReconcileModal } from 'pages/claims/components';
import { useActiveForm } from 'pages/claims/components/ClaimFormProvider';
import {
  buildTariffRowId,
  ClaimTableRow,
  getTariffDebits,
} from 'pages/claims/utils/claimTableRows';
// Constants
import { getSpecialtyCodes } from 'constants/specialtyCodes';
import {
  getIncisionFieldLabel,
  getIncisionOptions,
  getSplitFieldLabel,
  getSplitOptions,
} from 'constants/tariffConstants';
// Utils
import { isEqual } from 'lodash';
import { getValueByPath } from 'utils/getValueByPath';
import { calculateLocationServiceRate } from 'utils/overheadUtils';
import {
  CLAIM_TARIFF_STATUS,
  DEFAULT_CLAIM_TARIFF_STATUS,
} from '@billing/validation/tariff';
import { isAnesthesiaSpecialty } from '@billing/validation/specialty';
import {
  applyTariffTotalsWithOverrides,
  sumTariffTotals,
} from 'utils/tariffUtils';
import {
  isUnreconciledTariffStatus,
  isWriteOffEligibleTariffStatus,
} from 'utils/claimStatusUtils';
import { useTariffRowState } from './useTariffRowState';
import {
  getLatestPaidDebitByTariffID,
  preserveOrderByRowID,
  RowItem,
} from './utils/tariffHelpers';
import { TariffModalRow, TariffStagedAction } from 'columns/ModalTariffSelect';

export interface ModalTariffSelectProps {
  dirtyFields: React.MutableRefObject<{ [key: string]: boolean }>;
  stagedTariffActionsRef?: React.MutableRefObject<{
    claimID: string | null;
    hasStagedActions: boolean;
  }>;
  disabled?: boolean;
}

const getFieldPath = (prefix: string | undefined, field: string) =>
  prefix ? `${prefix}.${field}` : field;

const STAGED_TARIFF_ACTIONS_DIRTY_KEY = '__stagedTariffActions';

const BULK_TARIFF_STATUSES = [
  CLAIM_TARIFF_STATUS.READY,
  CLAIM_TARIFF_STATUS.INCOMPLETE,
  CLAIM_TARIFF_STATUS.REJECTED,
  CLAIM_TARIFF_STATUS.PAYOR_INVOICED,
  CLAIM_TARIFF_STATUS.UNINSURED,
  CLAIM_TARIFF_STATUS.WRITTEN_OFF,
  CLAIM_TARIFF_STATUS.CANCELLED,
  CLAIM_TARIFF_STATUS.PAID,
] as const;

type BulkTariffStatus = (typeof BULK_TARIFF_STATUSES)[number];

const PROTECTED_TARIFF_STATUSES = new Set<string>([
  CLAIM_TARIFF_STATUS.SUBMITTED,
  CLAIM_TARIFF_STATUS.PAID,
  CLAIM_TARIFF_STATUS.WRITTEN_OFF,
  CLAIM_TARIFF_STATUS.UNCLAIMABLE,
]);

interface BulkStatusUndo {
  statuses: Map<string, string>;
  overrides: Map<string, Partial<Tariff>>;
  stagedActions: Record<string, TariffStagedAction>;
  tariffsDirty: boolean | undefined;
  stagedDirty: boolean | undefined;
  stagedRef: { claimID: string | null; hasStagedActions: boolean } | undefined;
  items: RowItem[];
  unsaved: boolean;
}

const getRuleAppliedTariffState = (tariffs: Partial<RowItem>[]): string =>
  JSON.stringify(
    tariffs.map((tariff) => ({
      rowID: tariff._rowID,
      id: tariff.id,
      code: tariff.code,
      prefix: tariff.prefix,
      units: tariff.units,
      anesthesiaUnits: tariff.anesthesiaUnits,
      percentage: tariff.percentage,
      incision: tariff.incision,
    }))
  );

const focusTariffSearchInput = (rowID: string): void => {
  const tariffSearch = Array.from(
    document.querySelectorAll<HTMLInputElement>('[data-tariff-row-id]')
  ).find((input) => input.dataset.tariffRowId === rowID);

  tariffSearch?.focus();
};

const getAdjustmentAmount = (
  requestedAmount: string | number | null | undefined,
  paidAmount: string | number | null | undefined
) => {
  const requested = Number(requestedAmount ?? 0);
  const paid = Number(paidAmount ?? 0);
  const difference = requested - paid;
  if (Number.isNaN(difference)) return null;
  return difference.toFixed(2);
};

const getWriteOffAdjustment = (
  requestedAmount: string | number | null | undefined,
  paidAmount: string | number | null | undefined
) => {
  const adjustment = getAdjustmentAmount(requestedAmount, paidAmount);
  if (adjustment === null) return null;
  return Math.max(Number(adjustment), 0).toFixed(2);
};

export const ModalTariffSelect = memo<ModalTariffSelectProps>(
  ({ dirtyFields, stagedTariffActionsRef, disabled = false }) => {
    const { translate } = useTranslation();
    const { form, prefix } = useActiveForm();

    const formValues = form.getValues();

    const tariffPath = getFieldPath(prefix, 'tariffs');
    const debitsPath = getFieldPath(prefix, 'debits');
    const serviceStartPath = getFieldPath(prefix, 'serviceStart');
    const serviceEndPath = getFieldPath(prefix, 'serviceEnd');
    const payorPath = getFieldPath(prefix, 'payor');
    const icd9Path = getFieldPath(prefix, 'icd9');

    const claimID = getValueByPath(formValues, getFieldPath(prefix, 'id'));
    const provider: Provider = getValueByPath(
      formValues,
      getFieldPath(prefix, 'provider')
    );
    const specialtyCode = provider?.specialtyCode;
    const serviceStart: string = getValueByPath(formValues, serviceStartPath);
    const serviceEnd: string = getValueByPath(formValues, serviceEndPath);
    const payor: Payor | undefined = getValueByPath(formValues, payorPath);
    const defaultICD9 = getValueByPath(formValues, icd9Path);
    const province = payor?.province || 'MB';
    const provinceTariffFields = useMemo(
      () => ({
        splitOptions: getSplitOptions(province, translate),
        incisionOptions: getIncisionOptions(province, translate),
        splitLabel: getSplitFieldLabel(province, translate),
        incisionLabel: getIncisionFieldLabel(province, translate),
      }),
      [province, translate]
    );
    const virtualPhysicianRegion = getValueByPath(
      formValues,
      getFieldPath(prefix, 'virtualPhysicianRegion')
    );
    const locationOfService = getValueByPath(
      formValues,
      getFieldPath(prefix, 'locationOfService')
    );
    const hospitalCode = getValueByPath(
      formValues,
      getFieldPath(prefix, 'hospitalCode')
    );
    const serviceLocationIndicator = getValueByPath(
      formValues,
      getFieldPath(prefix, 'serviceLocationIndicator')
    );
    const debits: Debit[] =
      (getValueByPath(formValues, debitsPath) as Debit[]) || [];

    const [
      openConfirmDialog,
      { open: openConfirmDialogFunc, close: closeConfirmDialog },
    ] = useDisclosure(false);
    const [tariffToDelete, setTariffToDelete] = useState<RowItem | null>(null);
    const [pendingResubmissionDelete, setPendingResubmissionDelete] =
      useState(false);
    const [reconcileRows, setReconcileRows] = useState<ClaimTableRow[]>([]);
    const [reconcileOpen, setReconcileOpen] = useState(false);
    const [stagedActionByTariffID, setStagedActionByTariffID] = useState<
      Record<string, TariffStagedAction>
    >({});
    const [bulkStatusConfirmation, setBulkStatusConfirmation] =
      useState<BulkTariffStatus | null>(null);
    const [bulkStatusUndo, setBulkStatusUndo] = useState<BulkStatusUndo | null>(
      null
    );
    const [bulkStatusUnsaved, setBulkStatusUnsaved] = useState(false);
    const [editingTotalId, setEditingTotalId] = useState<string | null>(null);
    const [editingStartTimeId, setEditingStartTimeId] = useState<string | null>(
      null
    );
    const [editingEndTimeId, setEditingEndTimeId] = useState<string | null>(
      null
    );
    const initialTariffs: Tariff[] =
      (getValueByPath(formValues, tariffPath) as Tariff[]) || [];
    const reconciledTariffOverridesRef = useRef(
      new Map<string, Partial<Tariff>>()
    );
    const reconciledDebitOverridesRef = useRef(
      new Map<string, Partial<Debit>>()
    );

    const [tariffSearchFocusRequest, setTariffSearchFocusRequest] = useState<{
      rowID: string;
    } | null>(null);

    useEffect(() => {
      form.validateField(tariffPath);
    }, [form, tariffPath]);

    const markTariffsDirty = useCallback(() => {
      if (dirtyFields?.current) {
        dirtyFields.current.tariffs = true;
      }
      form.validateField(tariffPath);
    }, [dirtyFields, form, tariffPath]);

    const {
      items,
      setItems,
      hydrateRows,
      syncServiceStartFromClaim,
      syncServiceEndFromClaim,
      handleDeleteTariff,
      handleUnitsChange,
      handleAnesthesiaUnitsChange,
      handlePercentageChange,
      handleSplitChange,
      handleIncisionChange,
      handleStartTimeChange,
      handleEndTimeChange,
      handleTariffSelection,
      handleICD9Change,
      handleManualTotalSave,
      handleManualTotalReset,
      addBlankRow,
    } = useTariffRowState({
      initialTariffs: initialTariffs as RowItem[],
      claimServiceStart: serviceStart,
      claimServiceEnd: serviceEnd,
      specialtyCode,
      province,
      specialtyLookup: getSpecialtyCodes(province),
      hospitalCode,
      serviceLocationIndicator,
      defaultICD9,
      tariffPath,
      form,
      onDirty: markTariffsDirty,
    });
    const bulkRowIdentity = JSON.stringify(
      items.map(({ _rowID, id }) => [_rowID, id])
    );
    useEffect(() => {
      setBulkStatusUndo(null);
      setBulkStatusConfirmation(null);
    }, [bulkRowIdentity]);
    useEffect(() => {
      setBulkStatusUndo(null);
      setBulkStatusConfirmation(null);
      setBulkStatusUnsaved(false);
    }, [claimID]);
    const formTariffState = getRuleAppliedTariffState(initialTariffs);
    const lastFormTariffStateRef = useRef(formTariffState);
    const formTariffsChangedExternally =
      lastFormTariffStateRef.current !== formTariffState;
    if (formTariffsChangedExternally) {
      lastFormTariffStateRef.current = formTariffState;
    }

    useEffect(() => {
      if (!formTariffsChangedExternally) return;
      if (getRuleAppliedTariffState(items) === formTariffState) return;

      hydrateRows(initialTariffs as RowItem[]);
    }, [formTariffState, formTariffsChangedExternally, hydrateRows]);

    form.watch(tariffPath, ({ value }) => {
      const nextTariffs = Array.isArray(value)
        ? (value as Partial<RowItem>[])
        : [];
      const nextTariffState = getRuleAppliedTariffState(nextTariffs);
      if (lastFormTariffStateRef.current === nextTariffState) return;

      lastFormTariffStateRef.current = nextTariffState;
      if (getRuleAppliedTariffState(items) === nextTariffState) return;

      hydrateRows(nextTariffs as RowItem[]);
    });

    const focusTariffSearch = useCallback((rowID: string) => {
      setTariffSearchFocusRequest({ rowID });
    }, []);

    const handleAddBlankRow = useCallback((): string => {
      const existingBlankRow = [...items]
        .reverse()
        .find((item) => !item.code?.trim());

      if (existingBlankRow) {
        focusTariffSearch(existingBlankRow._rowID);
        return existingBlankRow._rowID;
      }

      const newRowID = addBlankRow();
      focusTariffSearch(newRowID);
      return newRowID;
    }, [addBlankRow, focusTariffSearch, items]);

    useEffect(() => {
      if (!tariffSearchFocusRequest) return;

      const focusTimer = window.setTimeout(() =>
        focusTariffSearchInput(tariffSearchFocusRequest.rowID)
      );

      return () => window.clearTimeout(focusTimer);
    }, [tariffSearchFocusRequest]);

    useEffect(() => {
      hydrateRows(initialTariffs as RowItem[]);
    }, [claimID]);

    form.watch(serviceStartPath, ({ previousValue, value }) => {
      if (value && previousValue !== value) {
        syncServiceStartFromClaim(value);
      }
    });

    form.watch(serviceEndPath, ({ previousValue, value }) => {
      if (value && previousValue !== value) {
        syncServiceEndFromClaim(value);
      }
    });

    const isUnreconciled = items.some((item) =>
      isUnreconciledTariffStatus(item.status)
    );

    const rowOrderMap = useMemo(
      () => new Map(items.map((item, index) => [item._rowID, index])),
      [items]
    );
    const tariffIdentityKey = useMemo(
      () =>
        items
          .map((item) => `${item._rowID}:${item.prefix}:${item.code}`)
          .join('|'),
      [items]
    );
    const tariffIdentityKeys = useMemo(() => items, [tariffIdentityKey]);

    const normalize = useCallback(
      (tariffs: Tariff[] = []) =>
        preserveOrderByRowID(tariffs as RowItem[], rowOrderMap),
      [rowOrderMap]
    );

    const locationServiceRate = useMemo(
      () =>
        calculateLocationServiceRate(
          virtualPhysicianRegion,
          locationOfService,
          province
        ),
      [virtualPhysicianRegion, locationOfService, province]
    );

    // Shared total-calculation path:
    // 1) compute tariff totals (including differential/premium logic),
    // 2) optionally apply debit overrides,
    // 3) normalize to current row order.
    const buildNormalizedTariffs = useCallback(
      (debitOverrides: Debit[]) => {
        const { modifier, claimLocation } = locationServiceRate;
        const tariffsWithCalculatedTotal = applyTariffTotalsWithOverrides(
          items as Tariff[],
          debitOverrides,
          modifier || 1,
          hospitalCode,
          serviceLocationIndicator,
          claimLocation,
          specialtyCode,
          province
        );

        const orderedTariffs = normalize(tariffsWithCalculatedTotal);
        if (reconciledTariffOverridesRef.current.size === 0) {
          return orderedTariffs;
        }

        return orderedTariffs.map((tariff) => ({
          ...tariff,
          ...reconciledTariffOverridesRef.current.get(tariff.id),
        }));
      },
      [
        hospitalCode,
        items,
        locationServiceRate,
        normalize,
        province,
        serviceLocationIndicator,
        specialtyCode,
      ]
    );

    // Table/form tariff rows. This path uses debit overrides where applicable.
    const normalizedTariffs = useMemo(
      () => buildNormalizedTariffs(debits),
      [buildNormalizedTariffs, debits]
    );

    const allocatedTotal = useMemo(() => {
      const allTariffsPaid =
        normalizedTariffs.length > 0 &&
        normalizedTariffs.every(
          (tariff) => tariff.status === CLAIM_TARIFF_STATUS.PAID
        );
      // Paid tariffs: sum latest paid debit once per tariff to avoid double-counting.
      if (allTariffsPaid) {
        const latestPaidDebitByTariffID = getLatestPaidDebitByTariffID(debits);
        return Array.from(latestPaidDebitByTariffID.values()).reduce(
          (acc, debit) => acc + Number(debit.paidAmount ?? 0),
          0
        );
      }

      // Non-paid claims: use tariff-computed totals only (no debit overrides),
      // so fee differential and hospital premium are always included.
      return sumTariffTotals(buildNormalizedTariffs([]));
    }, [buildNormalizedTariffs, debits, normalizedTariffs]);

    useEffect(() => {
      // Keep form tariffs synchronized with recalculated/normalized table rows.
      if (formTariffsChangedExternally) return;

      const current = normalize(
        (getValueByPath(form.getValues(), tariffPath) as Tariff[]) ?? []
      );
      const same =
        current.length === normalizedTariffs.length &&
        current.every((tariff, index) =>
          isEqual(tariff, normalizedTariffs[index])
        );
      if (same) return;
      lastFormTariffStateRef.current =
        getRuleAppliedTariffState(normalizedTariffs);
      form.setFieldValue(tariffPath, normalizedTariffs);
      form.validateField(tariffPath);
    }, [
      form,
      formTariffsChangedExternally,
      normalize,
      normalizedTariffs,
      tariffPath,
    ]);

    useEffect(() => {
      // Re-fetch tariff metadata when `specialtyCode` changes to keep row details accurate.
      // - For each row, searches tariffs using the code and current specialty/serviceStart.
      // - If a matching tariff is found, merges fresh data while preserving row-specific fields and `_rowID`.
      // - Preserves current visual order and avoids setting state if nothing changed.
      // - Uses a `cancelled` flag to ignore async results after unmount or dependency change.
      // Only refresh when tariff identity changes. Draft manual edits should not
      // trigger this effect or get lost when the refresh resolves.
      if (!specialtyCode || tariffIdentityKeys.length === 0) return;
      let cancelled = false;
      (async () => {
        const updated = await Promise.all(
          tariffIdentityKeys.map(async (item) => {
            const results = (await searchTariffs({
              searchValue: `${item.code}`,
              specialtyCode,
              serviceStart,
              province,
            })) as Tariff[] | undefined;

            const safeResults = Array.isArray(results) ? results : [];
            const found =
              safeResults.find((tariff) => tariff.id === item.id) ??
              safeResults.find(
                (tariff) =>
                  tariff.prefix === item.prefix && tariff.code === item.code
              );
            return {
              rowID: item._rowID,
              found,
            };
          })
        );

        if (cancelled) return;

        const updatedByRowID = new Map(
          updated.map(({ rowID, found }) => [rowID, found] as const)
        );

        setItems((prev) => {
          const merged = prev.map((item) => {
            const found = updatedByRowID.get(item._rowID);
            if (!found) {
              return item;
            }

            return {
              ...found,
              _rowID: item._rowID,
              units: item.units,
              anesthesiaUnits: item.anesthesiaUnits,
              isAnesthesiaUnitsManual: item.isAnesthesiaUnitsManual,
              icd9: item.icd9,
              percentage: item.percentage,
              split: item.split,
              incision: item.incision,
              total: item.total,
              isManualTotal: item.isManualTotal,
              serviceStart: item.serviceStart,
              serviceEnd: item.serviceEnd,
              isDirty: item.isDirty,
              serviceStartOverridden: item.serviceStartOverridden,
              serviceEndOverridden: item.serviceEndOverridden,
              status:
                item.status || found.status || DEFAULT_CLAIM_TARIFF_STATUS,
            } as RowItem;
          });

          const currentOrder = new Map(
            prev.map((item, index) => [item._rowID, index])
          );
          const next = preserveOrderByRowID(merged, currentOrder);

          return isEqual(next, prev) ? prev : (next as RowItem[]);
        });
      })();
      return () => {
        cancelled = true;
      };
    }, [
      serviceStart,
      province,
      setItems,
      specialtyCode,
      tariffIdentityKeys,
      tariffIdentityKey,
    ]);

    const requestDeleteTariff = useCallback(
      (row: RowItem, hasPendingResubmission: boolean) => {
        setTariffToDelete(row);
        setPendingResubmissionDelete(hasPendingResubmission);
        openConfirmDialogFunc();
      },
      [openConfirmDialogFunc]
    );

    const changeFn = useCallback(
      (stringifiedTariff: string, targetRowID: string) => {
        handleTariffSelection(
          JSON.parse(stringifiedTariff) as Tariff,
          targetRowID
        );
      },
      [handleTariffSelection]
    );

    const getCurrentClaim = useCallback((): Claim | undefined => {
      const values = form.getValues();
      return (prefix ? getValueByPath(values, prefix) : values) as
        | Claim
        | undefined;
    }, [form, prefix]);

    const markStagedTariffAction = useCallback(() => {
      if (dirtyFields?.current) {
        dirtyFields.current.tariffs = true;
        dirtyFields.current[STAGED_TARIFF_ACTIONS_DIRTY_KEY] = true;
      }
      if (stagedTariffActionsRef) {
        stagedTariffActionsRef.current = {
          claimID: claimID || null,
          hasStagedActions: true,
        };
      }
      form.validateField(tariffPath);
    }, [claimID, dirtyFields, form, stagedTariffActionsRef, tariffPath]);

    const applyLocalTariffStatus = useCallback(
      (
        tariffIDs: string[],
        status: string,
        stagedAction: TariffStagedAction
      ) => {
        setBulkStatusUndo(null);
        const tariffIDSet = new Set(tariffIDs);
        setItems((prev) =>
          prev.map((item) =>
            tariffIDSet.has(item.id) ? { ...item, status } : item
          )
        );
        setStagedActionByTariffID((prev) => {
          const next = { ...prev };
          for (const tariffID of tariffIDs) {
            next[tariffID] = stagedAction;
          }
          return next;
        });

        const currentTariffs: Tariff[] =
          (getValueByPath(form.getValues(), tariffPath) as Tariff[]) || [];
        for (const tariff of currentTariffs) {
          if (!tariffIDSet.has(tariff.id)) continue;
          reconciledTariffOverridesRef.current.set(tariff.id, {
            status,
            total: tariff.total,
          });
        }

        const updatedTariffs = currentTariffs.map((tariff) =>
          tariffIDSet.has(tariff.id) ? { ...tariff, status } : tariff
        );
        form.setFieldValue(tariffPath, updatedTariffs);
        markStagedTariffAction();
      },
      [form, markStagedTariffAction, setItems, tariffPath]
    );

    const eligibleBulkTariffs = normalizedTariffs.filter(
      (tariff) =>
        isTariff(tariff) && tariff.status !== CLAIM_TARIFF_STATUS.PENDING
    );
    const pendingBulkTariffs = normalizedTariffs.filter(
      (tariff) => tariff.status === CLAIM_TARIFF_STATUS.PENDING
    );
    const protectedBulkTariffs = eligibleBulkTariffs.filter((tariff) =>
      PROTECTED_TARIFF_STATUSES.has(tariff.status)
    );
    const bulkStatusLabel = (status: string): string =>
      translate(`pages:claims.bulkStatus.statuses.${status}`, status);

    const applyBulkStatus = (status: BulkTariffStatus): void => {
      setBulkStatusConfirmation(null);
      if (disabled || eligibleBulkTariffs.length === 0) return;
      const statuses = new Map(
        eligibleBulkTariffs.map((tariff) => [tariff.id, tariff.status])
      );
      if (eligibleBulkTariffs.every((tariff) => tariff.status === status))
        return;
      setBulkStatusUndo({
        statuses,
        overrides: new Map(reconciledTariffOverridesRef.current),
        stagedActions: { ...stagedActionByTariffID },
        tariffsDirty: dirtyFields.current.tariffs,
        stagedDirty: dirtyFields.current[STAGED_TARIFF_ACTIONS_DIRTY_KEY],
        stagedRef: stagedTariffActionsRef
          ? { ...stagedTariffActionsRef.current }
          : undefined,
        items,
        unsaved: bulkStatusUnsaved,
      });
      for (const tariff of eligibleBulkTariffs) {
        reconciledTariffOverridesRef.current.set(tariff.id, {
          ...reconciledTariffOverridesRef.current.get(tariff.id),
          status,
        });
      }
      setStagedActionByTariffID((previous) =>
        Object.fromEntries(
          Object.entries(previous).filter(
            ([tariffID]) => !statuses.has(tariffID)
          )
        )
      );
      setItems((previous) =>
        previous.map((tariff) =>
          statuses.has(tariff.id) ? { ...tariff, status } : tariff
        )
      );
      const currentTariffs: Tariff[] =
        getValueByPath(form.getValues(), tariffPath) ?? [];
      form.setFieldValue(
        tariffPath,
        currentTariffs.map((tariff) =>
          statuses.has(tariff.id) ? { ...tariff, status } : tariff
        )
      );
      setBulkStatusUnsaved(true);
      markStagedTariffAction();
    };

    const undoBulkStatus = (): void => {
      if (disabled || !bulkStatusUndo) return;
      const restoreStatus = <T extends Tariff>(tariff: T): T => {
        const status = bulkStatusUndo.statuses.get(tariff.id);
        return status === undefined ? tariff : { ...tariff, status };
      };
      reconciledTariffOverridesRef.current = new Map(bulkStatusUndo.overrides);
      setStagedActionByTariffID(bulkStatusUndo.stagedActions);
      setItems((previous) => previous.map(restoreStatus));
      const currentTariffs: Tariff[] =
        getValueByPath(form.getValues(), tariffPath) ?? [];
      form.setFieldValue(tariffPath, currentTariffs.map(restoreStatus));
      const restoreDirty = (key: string, value: boolean | undefined): void => {
        if (value === undefined) delete dirtyFields.current[key];
        else dirtyFields.current[key] = value;
      };
      restoreDirty(
        'tariffs',
        isEqual(items.map(restoreStatus), bulkStatusUndo.items)
          ? bulkStatusUndo.tariffsDirty
          : true
      );
      restoreDirty(STAGED_TARIFF_ACTIONS_DIRTY_KEY, bulkStatusUndo.stagedDirty);
      if (stagedTariffActionsRef && bulkStatusUndo.stagedRef) {
        stagedTariffActionsRef.current = bulkStatusUndo.stagedRef;
      }
      setBulkStatusUnsaved(bulkStatusUndo.unsaved);
      setBulkStatusUndo(null);
      form.validateField(tariffPath);
    };

    const stageReconcileTariffs = useCallback(
      ({
        tariffIDs,
        debitIDs,
        recordAdjustment,
      }: {
        tariffIDs: string[];
        debitIDs: string[];
        recordAdjustment: boolean;
      }) => {
        const tariffIDSet = new Set(tariffIDs);
        const currentTariffs: Tariff[] =
          (getValueByPath(form.getValues(), tariffPath) as Tariff[]) || [];
        const rejectedTariffIDs = currentTariffs
          .filter(
            (tariff) =>
              tariffIDSet.has(tariff.id) &&
              tariff.status === CLAIM_TARIFF_STATUS.REJECTED
          )
          .map((tariff) => tariff.id);
        const paymentTariffIDs = currentTariffs
          .filter(
            (tariff) =>
              tariffIDSet.has(tariff.id) &&
              isUnreconciledTariffStatus(tariff.status)
          )
          .map((tariff) => tariff.id);
        const paymentTariffIDSet = new Set(paymentTariffIDs);
        const debitIDSet = new Set(debitIDs);
        const currentDebits: Debit[] =
          (getValueByPath(form.getValues(), debitsPath) as Debit[]) || [];
        const latestPaidDebitByTariffID = getLatestPaidDebitByTariffID(
          currentDebits.filter((debit) => tariffIDSet.has(debit.tariffID))
        );
        const selectedDebitIDSet =
          debitIDSet.size > 0
            ? debitIDSet
            : new Set(
                Array.from(latestPaidDebitByTariffID.values()).map(
                  (debit) => debit.id
                )
              );
        if (recordAdjustment && paymentTariffIDs.length > 0) {
          const updatedDebits = currentDebits.map((debit) => {
            if (
              !paymentTariffIDSet.has(debit.tariffID) ||
              !selectedDebitIDSet.has(debit.id) ||
              !debit.paidAmount
            ) {
              return debit;
            }

            const requested = Number(debit.requestedAmount ?? 0);
            const paid = Number(debit.paidAmount ?? 0);
            const adjustment = (requested - paid).toFixed(2);
            const debitOverride = {
              requestedAmount: debit.paidAmount,
              fee: debit.paidAmount,
              adjustment,
            };
            reconciledDebitOverridesRef.current.set(debit.id, debitOverride);

            return {
              ...debit,
              ...debitOverride,
            };
          });
          form.setFieldValue(debitsPath, updatedDebits);
          if (dirtyFields?.current) {
            dirtyFields.current.debits = true;
          }
        }
        if (paymentTariffIDs.length > 0) {
          applyLocalTariffStatus(
            paymentTariffIDs,
            CLAIM_TARIFF_STATUS.PAID,
            'reconcile'
          );
        }
        if (rejectedTariffIDs.length > 0) {
          applyLocalTariffStatus(
            rejectedTariffIDs,
            CLAIM_TARIFF_STATUS.UNCLAIMABLE,
            'reconcile'
          );
        }
      },
      [applyLocalTariffStatus, debitsPath, dirtyFields, form, tariffPath]
    );

    const buildClaimTariffPreviewRow = useCallback(
      (row: TariffModalRow): ClaimTableRow | null => {
        const claim = getCurrentClaim();
        if (!claim?.id || !row.id) return null;

        return {
          ...claim,
          rowType: 'tariff' as const,
          rowId: buildTariffRowId(claim.id, row.id),
          parentClaimId: claim.id,
          parentClaim: claim,
          tariffId: row.id,
          tariffs: [row as Tariff],
          debits: row.debit ? [row.debit] : getTariffDebits(claim, row.id),
        } as ClaimTableRow;
      },
      [getCurrentClaim]
    );

    const handleMarkTariffAs = useCallback(
      async (row: TariffModalRow, status: string) => {
        setBulkStatusUndo(null);
        if (!row.id) {
          changeFn(JSON.stringify({ ...row, status }), row._rowID);
          return;
        }

        applyLocalTariffStatus(
          [row.id],
          status,
          status === CLAIM_TARIFF_STATUS.INCOMPLETE
            ? 'markIncomplete'
            : 'markReady'
        );
      },
      [applyLocalTariffStatus, changeFn]
    );

    const handleWriteOffTariff = useCallback(
      async (row: TariffModalRow) => {
        if (!row.id) return;
        const currentStatus = row.status?.trim() ?? '';
        if (!isWriteOffEligibleTariffStatus(currentStatus)) return;

        const currentDebits: Debit[] =
          (getValueByPath(form.getValues(), debitsPath) as Debit[]) || [];
        const updatedDebits = currentDebits.map((debit) => {
          if (debit.tariffID !== row.id) return debit;

          return {
            ...debit,
            adjustment: getWriteOffAdjustment(
              debit.requestedAmount,
              debit.paidAmount
            ),
          };
        });
        form.setFieldValue(debitsPath, updatedDebits);
        if (dirtyFields?.current) {
          dirtyFields.current.debits = true;
        }

        applyLocalTariffStatus(
          [row.id],
          CLAIM_TARIFF_STATUS.WRITTEN_OFF,
          'writeOff'
        );
      },
      [applyLocalTariffStatus, debitsPath, dirtyFields, form]
    );

    const handleResubmitTariffs = useCallback(
      async (rows: TariffModalRow[]) => {
        if (rows.length === 0) return;

        const tariffIDs = Array.from(
          new Set(rows.map((row) => row.id).filter(Boolean))
        );
        if (tariffIDs.length === 0) return;

        applyLocalTariffStatus(
          tariffIDs,
          CLAIM_TARIFF_STATUS.READY,
          'resubmit'
        );
      },
      [applyLocalTariffStatus]
    );

    const requestReconcileTariff = useCallback(
      (row: TariffModalRow) => {
        if (!claimID || !row.id) return;

        const previewRow = buildClaimTariffPreviewRow(row);
        if (!previewRow) return;

        setReconcileRows([previewRow]);
        setReconcileOpen(true);
      },
      [buildClaimTariffPreviewRow, claimID]
    );

    const handleConfirmReconcile = useCallback(
      async (recordAdjustment: boolean) => {
        if (!claimID || reconcileRows.length === 0) return;

        const tariffIDs = Array.from(
          new Set(reconcileRows.map((row) => row.tariffId).filter(Boolean))
        );
        if (tariffIDs.length === 0) return;

        stageReconcileTariffs({
          tariffIDs,
          debitIDs: reconcileRows.flatMap((row) =>
            (row.debits ?? []).map((debit) => debit.id)
          ),
          recordAdjustment,
        });
      },
      [claimID, reconcileRows, stageReconcileTariffs]
    );

    return (
      <>
        <div
          style={disabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
        >
          <Flex direction="column" gap="md" w="100%">
            <Flex align="center" gap="sm" wrap="wrap">
              <NativeSelect
                aria-label={translate('pages:claims.bulkStatus.label')}
                value=""
                disabled={disabled || eligibleBulkTariffs.length === 0}
                data={[
                  {
                    value: '',
                    label: translate('pages:claims.bulkStatus.label'),
                    disabled: true,
                  },
                  ...BULK_TARIFF_STATUSES.map((status) => ({
                    value: status,
                    label: bulkStatusLabel(status),
                  })),
                ]}
                onChange={(event) => {
                  const status = BULK_TARIFF_STATUSES.find(
                    (value) => value === event.currentTarget.value
                  );
                  if (!status || disabled) return;
                  if (
                    protectedBulkTariffs.length > 0 ||
                    PROTECTED_TARIFF_STATUSES.has(status)
                  ) {
                    setBulkStatusConfirmation(status);
                  } else applyBulkStatus(status);
                }}
              />
              {bulkStatusUnsaved && (
                <Text size="sm" c="dimmed">
                  {translate('pages:claims.bulkStatus.unsaved')}
                </Text>
              )}
              {bulkStatusUndo && (
                <Button
                  variant="subtle"
                  disabled={disabled}
                  onClick={undoBulkStatus}
                >
                  {translate('pages:claims.bulkStatus.undo')}
                </Button>
              )}
              {pendingBulkTariffs.length > 0 && (
                <Text size="sm" c="dimmed">
                  {translate('pages:claims.bulkStatus.pendingExcluded')}
                </Text>
              )}
            </Flex>
            {items.length === 0 ? (
              <>
                <Text c="dimmed">No tariffs selected</Text>
                <Flex
                  justify="flex-start"
                  align="flex-start"
                  direction="row"
                  wrap="wrap"
                >
                  <Button
                    variant="outline"
                    radius="md"
                    color="blue"
                    data-testid="add-tariff-button"
                    onClick={handleAddBlankRow}
                  >
                    <IconPlus size={16} />
                    Add
                  </Button>
                </Flex>
              </>
            ) : (
              <ModalTariffSelectTable
                rows={normalizedTariffs as RowItem[]}
                items={items}
                locationServiceRate={locationServiceRate}
                debits={debits}
                stagedActionByTariffID={stagedActionByTariffID}
                isUnreconciled={isUnreconciled}
                providerID={provider?.id}
                isAnesthesiaProvider={isAnesthesiaSpecialty(
                  province,
                  specialtyCode
                )}
                hospitalCode={hospitalCode}
                serviceLocationIndicator={serviceLocationIndicator}
                splitOptions={provinceTariffFields.splitOptions}
                incisionOptions={provinceTariffFields.incisionOptions}
                splitLabel={provinceTariffFields.splitLabel}
                incisionLabel={provinceTariffFields.incisionLabel}
                editingTotalId={editingTotalId}
                editingStartTimeId={editingStartTimeId}
                editingEndTimeId={editingEndTimeId}
                allocatedTotal={allocatedTotal}
                setEditingTotalId={setEditingTotalId}
                setEditingStartTimeId={setEditingStartTimeId}
                setEditingEndTimeId={setEditingEndTimeId}
                changeFn={changeFn}
                handleUnitsChange={handleUnitsChange}
                handleAnesthesiaUnitsChange={handleAnesthesiaUnitsChange}
                handleStartTimeChange={handleStartTimeChange}
                handleEndTimeChange={handleEndTimeChange}
                handleICD9Change={handleICD9Change}
                handleSplitChange={handleSplitChange}
                handleIncisionChange={handleIncisionChange}
                onCommentSave={(rowID, comment) => {
                  setItems((previous) =>
                    previous.map((item) =>
                      item._rowID === rowID
                        ? { ...item, pendingComment: comment }
                        : item
                    )
                  );
                  markTariffsDirty();
                }}
                handlePercentageChange={handlePercentageChange}
                handleManualTotalSave={handleManualTotalSave}
                handleManualTotalReset={handleManualTotalReset}
                reconcileTariff={requestReconcileTariff}
                writeOffTariff={handleWriteOffTariff}
                resubmitTariffs={handleResubmitTariffs}
                markTariffAs={handleMarkTariffAs}
                addBlankRow={handleAddBlankRow}
                requestDeleteTariff={requestDeleteTariff}
              />
            )}
          </Flex>
        </div>
        <ConfirmDialog
          open={bulkStatusConfirmation !== null}
          isLoading={false}
          onClose={() => setBulkStatusConfirmation(null)}
          onConfirm={() => {
            if (bulkStatusConfirmation) applyBulkStatus(bulkStatusConfirmation);
          }}
          title={translate('pages:claims.bulkStatus.confirmTitle')}
          body={[
            translate('pages:claims.bulkStatus.confirmBody', {
              count: eligibleBulkTariffs.length,
              status: bulkStatusLabel(bulkStatusConfirmation ?? ''),
            }),
            bulkStatusConfirmation === CLAIM_TARIFF_STATUS.PAID
              ? translate('pages:claims.bulkStatus.paidWarning')
              : '',
            protectedBulkTariffs.length > 0
              ? translate('pages:claims.bulkStatus.protectedRows', {
                  tariffs: protectedBulkTariffs
                    .map(
                      (tariff) =>
                        `${tariff.code} (${bulkStatusLabel(tariff.status)})`
                    )
                    .join(', '),
                })
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
          confirmText={translate('pages:claims.bulkStatus.confirm')}
        />
        <ConfirmDialog
          open={openConfirmDialog}
          isLoading={false}
          onClose={() => {
            closeConfirmDialog();
            setPendingResubmissionDelete(false);
            setTariffToDelete(null);
          }}
          onConfirm={() => {
            if (tariffToDelete?._rowID) {
              handleDeleteTariff(tariffToDelete._rowID);
            }
            closeConfirmDialog();
            setPendingResubmissionDelete(false);
            setTariffToDelete(null);
          }}
          title={translate('pages:claims.deleteTariffTitle', 'Delete Tariff')}
          body={
            pendingResubmissionDelete
              ? translate(
                  'pages:claims.deleteTariffPendingResubmissionBody',
                  'Tariff has been marked for resubmission, are you sure you want to delete it?'
                )
              : translate(
                  'pages:claims.deleteTariffBody',
                  'Are you sure you want to delete this tariff from the claim?'
                )
          }
          confirmText={translate('buttons.delete', 'Delete')}
        />
        <ClaimBulkReconcileModal
          open={reconcileOpen}
          rows={reconcileRows}
          onClose={() => {
            setReconcileOpen(false);
            setReconcileRows([]);
          }}
          onConfirm={handleConfirmReconcile}
        />
      </>
    );
  }
);
